import { useState, useRef, MutableRefObject } from 'react';
import { createGlobalStore } from 'hox';
import { IQaRecordItem } from '../components/qa-render/qa-records/index';
import { IFeedbackLabelItem, ILucaAskConfigInfo, ILucaAskDetail, IProjectConfig, TResourceType } from '../types';
import { toastInQa } from '@/sub-luca-plugin/utils/qa';
import { getUuid } from '../utils';
import { cancelTokenSource, addRequestControllerFn, interruptAllExcuteFn } from './requestTask';
import { ICustomAudioRef } from '../compatible-components';

interface IAdditionalInfo {
    /**
     * 初始资源类型 视频：1、题目：2、直播：5（用于配置合并不被覆盖）
     */
    initResourceType: TResourceType;
    /**
     * 初始资源id（用于配置合并不被覆盖）
     */
    initResourceId: number;
    /**
     * 外部传入的初始课程Id（用于配置合并不被覆盖）
     */
    initCourseId: number;
    /**
     * 外部传入或者新建的初始化项目id（用于配置合并不被覆盖）
     */
    initProjectId: number;
    /**
     * 外部传入或者新建的初始化科目id（用于配置合并不被覆盖）
     */
    initSubjectId: number;
    /**
     * 外部传入或者新建的初始化二级科目id（用于配置合并不被覆盖）
     */
    initTwoLevelSubjectId: number;
    /**
     * lucaId
     */
    lucaId: number;
    /**
     * 资源类型 视频：1、题目：2、直播：5
     */
    resourceType: TResourceType;
    /**
     * 资源id
     */
    resourceId: number;
    /**
     * 课程Id（用于配置合并不被覆盖）
     */
    courseId: number;
    /**
     * 课程类型 ep3包含（13）
     */
    courseType: 13;
    /**
     * 渠道标记 固定：gaodun
     */
    sourceMark: 'gaodun';
    /**
     * 客户端来源 固定：gaodun-pc
     */
    clientSource: 'gaodun-pc';
    /**
     * 提问入口
     */
    entrySource: string;
    /**
     * 业务场景 课程：1、题库：2、学习空间：8
     */
    sceneSource: number;
    /**
     * 课程大纲上资源关联的条目id
     */
    csItemId: number;
    /**
     * botId
     */
    botId: string;
    /**
     * 显示创建新答疑按钮
     */
    showCreateNewQaBtn: boolean;
    /**
     * 显示是否解决
     */
    showWhetherSolve: boolean;
    /**
     * 是否可执行动效
     */
    canExcuteAimation: boolean;
    /**
     * 显示拍题答疑按钮
     */
    showSearchQuestionsByOcrBtn: boolean;
    /**
     * 显示学科搜索标签
     */
    showSubjectSearchLabel: boolean;
    /**
     * 禁止初始化项目、一级科目、二级科目不能反选到哪一级
     */
    disabledInitReverseSelectLevel: number;
    /**
     * 用于问题总结
     */
    questionBotKey: string;
    /**
     * 用于用户提问AI
     */
    botKey: string;
    /**
     * 通过useEffect刷新历史会话的依赖
     */
    refreshCommRecordsByEffect: string;
    /**
     * 题目场景，当前问题的问题Id
     */
    questionId: number;
    /**
     * AI回复反馈的选择标签列表
     */
    feedbackLabelList: IFeedbackLabelItem[];
}

type IQaBasicConfig = Partial<Omit<IAdditionalInfo, 'botId' | 'showWhetherSolve'>>;

type IQaConfig = Partial<ILucaAskDetail & ILucaAskConfigInfo & IAdditionalInfo>;

type TLockStatus = 'replying' | 'ocrAndGptReplying';

export const [useQaStore, getQaStore] = createGlobalStore(() => {
    // 用户是否能操作的锁
    const lock = useRef<Record<TLockStatus, number>>({ replying: 0, ocrAndGptReplying: 0 });
    // 是否允许滚动到底部
    const allowScrollToBottom = useRef(true);
    // 全局使用的项目科目选择的数据
    const projects = useRef<IProjectConfig[]>([]);
    // 中断执行的记录
    const isContinueExcuteRecords = useRef<Record<string, boolean>>({});
    // 请求控制器
    const requestControllers = useRef<Array<cancelTokenSource>>([]);
    // 执行计时标记
    const excuteTimer = useRef<ReturnType<typeof setTimeout>>();
    // 存储渲染的所有的audio句柄
    const audioRefs = useRef<MutableRefObject<ICustomAudioRef | undefined>[]>([]);
    // 初始化配置数据
    // 这里的初始化参数暂时写死，是有数据的
    const qaBasicConfig = useRef<IQaBasicConfig>({});
    const [qaRecords, setQaRecords] = useState<IQaRecordItem[]>([]);
    const [qaConfig, setQaConfig] = useState<IQaConfig>({ showWhetherSolve: false });

    const clearQaStore = (isClearAllConfig = false) => {
        if (isClearAllConfig) {
            qaBasicConfig.current = {};
            setQaConfig({});
        } else {
            updateQaConfig({ canExcuteAimation: false, collectFlag: undefined, askChatTitle: '', questionState: 0 });
        }
        setQaRecords([]);
        lock.current = { replying: 0, ocrAndGptReplying: 0 };
        audioRefs.current = [];
    };

    const updateQaBasicConfig = (config: IQaBasicConfig) => (qaBasicConfig.current = config);

    const updateQaConfig = (currentConfig: IQaConfig, isCover = false) => {
        setQaConfig(oldConfig => {
            const newConfig = isCover
                ? { ...qaBasicConfig.current, ...currentConfig }
                : { ...qaBasicConfig.current, ...oldConfig, ...currentConfig };
            console.log('updateQaConfig', newConfig);
            return newConfig;
        });
    };

    const validateCanSend = (data: TLockStatus | Array<TLockStatus>) => {
        if (typeof data === 'string' && lock.current[data] > 0) {
            toastInQa('Luca正在回复，请稍后再试');
            return false;
        }
        if (typeof data !== 'string' && data.some(key => lock.current[key] > 0)) {
            toastInQa('Luca正在回复，请稍后再试');
            return false;
        }
        return true;
    };

    const setLockStatus = (status: TLockStatus) => {
        lock.current[status]++;
    };

    const clearLockStatus = (status: TLockStatus) => {
        lock.current[status]--;
    };

    const filterQaRecords = (filterRecordType: Record<string, boolean>) => {
        setQaRecords(list => {
            return list.filter(item => !filterRecordType[item.type]);
        });
    };

    const pushQaRecords = (
        data: IQaRecordItem | IQaRecordItem[],
        options?: { deleteLaterNum?: number; filterRecordType?: Record<string, boolean>; resetPermanentActionBarStatus?: boolean }
    ) => {
        // console.log('pushQaRecords', data, options);
        const { deleteLaterNum, filterRecordType, resetPermanentActionBarStatus } = options || {};
        const newData = Array.isArray(data) ? data : [data];
        if (!newData.length) return;
        setQaRecords(list => {
            let oldData = [...list];
            if (deleteLaterNum) {
                oldData = oldData.slice(0, list.length - deleteLaterNum);
            }
            if (filterRecordType) {
                oldData = oldData.filter(item => !filterRecordType[item.type]);
            }
            if (resetPermanentActionBarStatus) {
                oldData = oldData.map(item => {
                    if (item.type !== 'ai') return item;
                    return { ...item, permanentActionBar: false };
                });
            }
            return oldData.concat(newData);
        });
    };

    const isAllowScrollToBottom = () => {
        const currentStatus = allowScrollToBottom.current;
        allowScrollToBottom.current = true;
        return currentStatus;
    };

    const updateQaRecords = (mapCallback: (oldRecords: IQaRecordItem[]) => IQaRecordItem[], preventScrollToBottom = true) => {
        if (preventScrollToBottom) allowScrollToBottom.current = false;
        setQaRecords(mapCallback);
    };

    const deleteLastQaRecord = () => setQaRecords(oldList => oldList.slice(0, oldList.length - 1));

    const deleteQaRecordByRecordId = (recordId: string) => {
        setQaRecords(oldList => oldList.filter(item => item.recordId !== recordId));
    };

    // 更新最后一条用户的发送信息的RecordId
    const updateRecordIdOfLastUserMessage = (recordId: string) => {
        setQaRecords(oldList =>
            oldList.map((item, index) => {
                if (index < oldList.length - 1 || !item.recordId.includes('user')) return item;
                return { ...item, recordId };
            })
        );
    };

    const getExcuteProcessId = () => {
        const uuid = getUuid();
        isContinueExcuteRecords.current[uuid] = true;
        return uuid;
    };
    const isContinueExcute = (id?: string) => {
        return id ? !!isContinueExcuteRecords.current[id] : true;
    };
    const deleteExcuteRecord = (id?: string) => {
        id && delete isContinueExcuteRecords.current[id];
    };
    // 开启执行计时
    const openExcuteTimer = (time = 10000) => {
        clearTimeout(excuteTimer.current);
        excuteTimer.current = setTimeout(() => interruptAllExcute('timeout_cancel'), time);
    };
    // 添加请求控制
    const addRequestController = (type: 'axios' | 'fetch') => {
        return addRequestControllerFn<'axios'>('axios', requestControllers);
    };

    // 终止所有执行
    const interruptAllExcute = (reason: 'timeout_cancel' | 'send_cancel') => {
        interruptAllExcuteFn(reason, {
            requestControllers,
            excuteTimer,
            isContinueExcuteRecords,
        });
    };

    const addProjects = (list: IProjectConfig[]) => {
        projects.current = list;
    };

    const getProjects = () => projects.current;

    const addAudioRef = (ref: MutableRefObject<ICustomAudioRef | undefined>) => {
        audioRefs.current.push(ref);
    };
    const pauseAllAudios = () => {
        audioRefs.current.forEach(ref => {
            ref.current?.pause();
        });
    };

    return {
        addAudioRef,
        pauseAllAudios,
        qaRecords,
        pushQaRecords,
        deleteLastQaRecord,
        updateRecordIdOfLastUserMessage,
        validateCanSend,
        setLockStatus,
        clearLockStatus,
        qaConfig,
        updateQaConfig,
        deleteQaRecordByRecordId,
        clearQaStore,
        filterQaRecords,
        setQaRecords,
        updateQaBasicConfig,
        qaBasicConfig,
        addRequestController,
        interruptAllExcute,
        openExcuteTimer,
        getExcuteProcessId,
        isContinueExcute,
        deleteExcuteRecord,
        updateQaRecords,
        isAllowScrollToBottom,
        addProjects,
        getProjects,
    };
});
